// src/modules/tesisat/utils/buatTypeUtils.js
//
// BUAT TİPİ KURALI (temel): bir buat üzerinde bağlı hat sayısı
//   ≤ 3  → YUVARLAK buat (buatCircle / buatLightingCircle)
//   > 3  → KARE buat    (buatSquare / buatLightingSquare)
//
// "Hat" = buat noktasına gelen wire segment'leri (priz/aydınlatma/armatür hatları)
//   + priz/anahtar SAP'ı (sembol geometrisinin parçası, ayrı obje değil → +1).
// Sayım GEOMETRİK yapılır (konumdan), wireMap dizilerine güvenilmez. Bir wire'ın
// buat noktasındaki vertex'i iç noktaysa 2 segment (gelen+giden), uçsa 1 segment.
//
// Tip değişimi mesh'i komple değiştirmeden YERİNDE geometry swap ile yapılır
// (parent/position/userData korunur → düşük risk).

import * as THREE from 'three';

const SOCKET_BUAT = new Set(['socketBuat', 'buatCircle', 'buatSquare']);
const LIGHTING_BUAT = new Set(['lightingBuat', 'lightBuat', 'buatLightingCircle', 'buatLightingSquare']);
// Buat noktasına bağlanabilen hat tipleri (priz/aydınlatma + armatür eğrisi).
const COUNT_WIRE_TYPES = new Set(['wireSocket', 'wireLighting', 'curvedLine']);
// Sap taşıyan semboller (sap = sembolün line1 geometrisi, ayrı hat objesi değil → +1).
const SAP_TYPES = new Set(['socket', 'LightSwitch']);
const TOL = 5; // buat ile wire vertex/sembol konumu eşleşme toleransı (world birim)

function isSocketBuat(t) { return SOCKET_BUAT.has(t); }
function isLightingBuat(t) { return LIGHTING_BUAT.has(t); }
export function isBuatType(t) { return isSocketBuat(t) || isLightingBuat(t); }

/**
 * Buat noktasındaki toplam hat sayısını GEOMETRİK olarak hesaplar.
 */
export function countLinesAtBuat(buat, scene) {
  if (!buat || !scene) return 0;
  buat.updateWorldMatrix(true, false);
  const P = new THREE.Vector3();
  buat.getWorldPosition(P);

  let count = 0;
  const wp = new THREE.Vector3();

  scene.traverse((o) => {
    if (o === buat) return;
    const ud = o.userData;
    if (!ud) return;
    const t = ud.type;
    const isWire = (typeof t === 'string' && COUNT_WIRE_TYPES.has(t)) || ud.function === 'wireLightingFixture';

    if (isWire && o.geometry?.attributes?.position) {
      o.updateWorldMatrix(true, false);
      const pos = o.geometry.attributes.position;
      const n = pos.count;
      for (let i = 0; i < n; i++) {
        wp.fromBufferAttribute(pos, i).applyMatrix4(o.matrixWorld);
        if (wp.distanceTo(P) <= TOL) {
          // İç vertex (gelen+giden) → 2 segment; uç vertex → 1 segment.
          count += (i === 0 || i === n - 1) ? 1 : 2;
        }
      }
    } else if (typeof t === 'string' && SAP_TYPES.has(t)) {
      // Priz/anahtar sembolü buat noktasında ise sap'ı 1 hat sayılır.
      o.getWorldPosition(wp);
      if (wp.distanceTo(P) <= TOL) count += 1;
    }
  });

  return count;
}

/**
 * Buat'ın tipini hat sayısına göre günceller (yerinde geometry swap).
 * count ≤ 3 → yuvarlak, > 3 → kare. count < 3 ise (buat zaten olmamalı) dokunmaz.
 */
export function updateBuatType(buat, scene) {
  if (!buat || !scene) return;
  const cur = buat.userData?.type;
  if (!isBuatType(cur)) return;

  const count = countLinesAtBuat(buat, scene);
  if (count < 3) return; // ≤2: kural buat öngörmüyor, dokunma

  const shouldSquare = count > 3;
  const isSquareNow = cur === 'buatSquare' || cur === 'buatLightingSquare';
  if (shouldSquare === isSquareNow) return; // zaten doğru tip

  const lighting = isLightingBuat(cur);
  const newType = lighting
    ? (shouldSquare ? 'buatLightingSquare' : 'buatLightingCircle')
    : (shouldSquare ? 'buatSquare' : 'buatCircle');

  // Yerinde geometry swap (mesh/parent/position/userData korunur).
  const oldGeom = buat.geometry;
  buat.geometry = shouldSquare
    ? new THREE.PlaneGeometry(5, 5)
    : new THREE.CircleGeometry(2, 32);
  if (oldGeom && oldGeom !== buat.geometry) oldGeom.dispose();

  buat.userData.type = newType;
  buat.userData.buatType = shouldSquare ? 'square' : 'circle';
}

/**
 * Sahnedeki tüm buat'ları hat sayısına göre günceller. Wire/fixture/socket
 * ekleme sonrası çağrılır (sadece ekleme olaylarında — per-frame değil).
 */
export function updateAllBuatTypes(scene) {
  if (!scene) return;
  const buats = [];
  scene.traverse((o) => { if (isBuatType(o.userData?.type)) buats.push(o); });
  for (const b of buats) updateBuatType(b, scene);
}
